import { useState, useRef, useMemo } from "react";
import systemMap from "../data/dls-system-map.json";

const NAVY = "#1B3A5C";
const STEEL = "#4A6D8C";
const SLATE_300 = "#CBD5E1";
const SLATE_400 = "#94A3B8";
const SLATE_700 = "#334155";

const RADIUS_STEP = 180;
const VIEWBOX = { x: -550, y: -400, w: 1100, h: 800 };

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// Build a positioned tree from the data + which nodes are expanded.
// Root is always rendered as "expanded" so its children (depth 1) are visible.
function buildLayout(rootData, expanded) {
  function visit(node, path, angleStart, angleEnd, depth) {
    const angle = (angleStart + angleEnd) / 2;
    const radius = depth * RADIUS_STEP;
    const isExpanded = path === "" || expanded.has(path);
    const hasChildren = !!(node.children && node.children.length > 0);

    const result = {
      key: path === "" ? "_root" : path,
      path,
      text: node.text,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      angle,
      radius,
      depth,
      hasChildren,
      isExpanded,
      children: [],
    };

    if (hasChildren && isExpanded) {
      const slice = (angleEnd - angleStart) / node.children.length;
      node.children.forEach((child, i) => {
        const childPath = path === "" ? String(i) : `${path}.${i}`;
        result.children.push(
          visit(
            child,
            childPath,
            angleStart + i * slice,
            angleStart + (i + 1) * slice,
            depth + 1
          )
        );
      });
    }

    return result;
  }

  // Start the first branch pointing up (-π/2) so the layout reads naturally.
  return visit(rootData, "", -Math.PI / 2, (3 * Math.PI) / 2, 0);
}

function flattenNodes(node, list = []) {
  list.push(node);
  for (const child of node.children) flattenNodes(child, list);
  return list;
}

function flattenEdges(node, list = []) {
  for (const child of node.children) {
    list.push({ from: node, to: child });
    flattenEdges(child, list);
  }
  return list;
}

function MapNode({ node, onToggle }) {
  const isRoot = node.depth === 0;

  const angleDeg = (node.angle * 180) / Math.PI;
  const normAngle = ((angleDeg % 360) + 360) % 360;
  const flip = normAngle > 90 && normAngle < 270;
  const rotation = flip ? normAngle + 180 : normAngle;
  const textAnchor = flip ? "end" : "start";

  const labelOffset = isRoot ? 0 : 12;
  const labelX = node.x + Math.cos(node.angle) * labelOffset;
  const labelY = node.y + Math.sin(node.angle) * labelOffset;

  const dotRadius = isRoot ? 11 : node.hasChildren ? 6 : 4;
  const fill = isRoot
    ? NAVY
    : node.hasChildren
    ? node.isExpanded
      ? NAVY
      : STEEL
    : SLATE_400;

  const fontSize = isRoot ? 13 : node.depth === 1 ? 12 : node.depth === 2 ? 11 : 10;
  const fontWeight = isRoot ? 600 : node.depth === 1 ? 500 : 400;
  const truncLen = isRoot ? 30 : node.depth === 1 ? 24 : node.depth === 2 ? 22 : 18;

  return (
    <g>
      {/* Hit area — wider than the visible dot for easier tapping */}
      {node.hasChildren && (
        <circle
          cx={node.x}
          cy={node.y}
          r={Math.max(dotRadius + 6, 14)}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.path);
          }}
        />
      )}
      <circle
        cx={node.x}
        cy={node.y}
        r={dotRadius}
        fill={fill}
        stroke="white"
        strokeWidth={2}
        pointerEvents="none"
      />
      {isRoot ? (
        <text
          x={node.x}
          y={node.y + dotRadius + 18}
          fontSize={fontSize}
          fontWeight={fontWeight}
          textAnchor="middle"
          fill={NAVY}
          pointerEvents="none"
        >
          {truncate(node.text, truncLen)}
        </text>
      ) : (
        <text
          x={labelX}
          y={labelY}
          fontSize={fontSize}
          fontWeight={fontWeight}
          fill={SLATE_700}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          transform={`rotate(${rotation} ${labelX} ${labelY})`}
          pointerEvents="none"
        >
          {truncate(node.text, truncLen)}
        </text>
      )}
    </g>
  );
}

function MapButton({ children, onClick, title, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="w-9 h-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition-colors"
    >
      {children}
    </button>
  );
}

export default function SystemMapView() {
  const [expanded, setExpanded] = useState(() => new Set());
  const [transform, setTransform] = useState({ tx: 0, ty: 0, scale: 1 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const svgRef = useRef(null);

  function toggle(path) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  function resetView() {
    setTransform({ tx: 0, ty: 0, scale: 1 });
  }

  function zoomIn() {
    setTransform((t) => ({ ...t, scale: Math.min(3, t.scale * 1.2) }));
  }

  function zoomOut() {
    setTransform((t) => ({ ...t, scale: Math.max(0.3, t.scale / 1.2) }));
  }

  const layout = useMemo(() => buildLayout(systemMap, expanded), [expanded]);
  const nodes = useMemo(() => flattenNodes(layout), [layout]);
  const edges = useMemo(() => flattenEdges(layout), [layout]);

  // Single-pointer drag = pan.
  // Two-pointer pinch = zoom.
  const activePointers = useRef(new Map());

  function onPointerDown(e) {
    if (e.target && e.target.tagName === "circle" && e.target.style.cursor === "pointer") {
      // Don't start a drag from a tappable node circle — let its click fire.
      return;
    }
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);

    if (activePointers.current.size === 1) {
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origTx: transform.tx,
        origTy: transform.ty,
      };
    } else if (activePointers.current.size === 2) {
      // Switch to pinch
      dragRef.current = null;
      const [a, b] = Array.from(activePointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      pinchRef.current = { startDist: dist, origScale: transform.scale };
    }
  }

  function onPointerMove(e) {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.current.size === 2 && pinchRef.current) {
      const [a, b] = Array.from(activePointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const factor = dist / pinchRef.current.startDist;
      setTransform((t) => ({
        ...t,
        scale: Math.max(0.3, Math.min(3, pinchRef.current.origScale * factor)),
      }));
    } else if (activePointers.current.size === 1 && dragRef.current) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setTransform((t) => ({
        ...t,
        tx: dragRef.current.origTx + dx,
        ty: dragRef.current.origTy + dy,
      }));
    }
  }

  function onPointerUp(e) {
    activePointers.current.delete(e.pointerId);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    if (activePointers.current.size < 2) pinchRef.current = null;
    if (activePointers.current.size < 1) dragRef.current = null;
  }

  function onWheel(e) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setTransform((t) => ({
      ...t,
      scale: Math.max(0.3, Math.min(3, t.scale * factor)),
    }));
  }

  const hasExpanded = expanded.size > 0;

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 tracking-tight mb-1 px-1">
        System Map
      </h2>
      <p className="text-sm text-slate-500 mb-4 px-1">
        Drag to pan · Tap a node to expand · Scroll or pinch to zoom
      </p>

      <div className="relative bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
        <svg
          ref={svgRef}
          className="block w-full"
          viewBox={`${VIEWBOX.x} ${VIEWBOX.y} ${VIEWBOX.w} ${VIEWBOX.h}`}
          style={{
            height: "min(70vh, 640px)",
            touchAction: "none",
            cursor: dragRef.current ? "grabbing" : "grab",
            userSelect: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
        >
          <g
            transform={`translate(${transform.tx}, ${transform.ty}) scale(${transform.scale})`}
          >
            {edges.map((edge, i) => (
              <line
                key={`e${i}`}
                x1={edge.from.x}
                y1={edge.from.y}
                x2={edge.to.x}
                y2={edge.to.y}
                stroke={edge.to.depth === 1 ? STEEL : SLATE_300}
                strokeWidth={edge.to.depth === 1 ? 1.5 : 1}
                pointerEvents="none"
              />
            ))}
            {nodes.map((node) => (
              <MapNode key={node.key} node={node} onToggle={toggle} />
            ))}
          </g>
        </svg>

        {/* Controls */}
        <div className="absolute top-3 right-3 flex flex-col bg-white rounded-lg border border-slate-200/70 shadow-sm divide-y divide-slate-100">
          <MapButton onClick={zoomIn} title="Zoom in">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </MapButton>
          <MapButton onClick={zoomOut} title="Zoom out">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </MapButton>
          <MapButton onClick={resetView} title="Reset view">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M8 16H3v5" />
            </svg>
          </MapButton>
        </div>

        {hasExpanded && (
          <button
            onClick={collapseAll}
            className="absolute bottom-3 right-3 text-xs bg-white border border-slate-200/70 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg shadow-sm transition-colors"
          >
            Collapse all
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-4 text-center">
        v2 — radial mind map. {nodes.filter((n) => !n.hasChildren || !n.isExpanded).length} nodes
        visible.
      </p>
    </div>
  );
}
